/**
 * Policies & Procedures (T-93): the team's written policies, grouped into Sections, with a Version
 * kept for every save.
 */

export const POLICY_KINDS = ['policy', 'procedure'] as const;
export type PolicyKind = (typeof POLICY_KINDS)[number];

export const POLICY_STATUSES = ['draft', 'needs_decision', 'agreed'] as const;
export type PolicyStatus = (typeof POLICY_STATUSES)[number];

export const POLICY_KIND_LABELS: Record<PolicyKind, string> = { policy: 'Policy', procedure: 'Procedure' };
export const POLICY_STATUS_LABELS: Record<PolicyStatus, string> = {
  draft: 'Draft',
  needs_decision: 'Needs decision',
  agreed: 'Agreed',
};

export function isPolicyKind(value: unknown): value is PolicyKind {
  return typeof value === 'string' && (POLICY_KINDS as readonly string[]).includes(value);
}

export function isPolicyStatus(value: unknown): value is PolicyStatus {
  return typeof value === 'string' && (POLICY_STATUSES as readonly string[]).includes(value);
}

export interface PolicySection {
  id: string;
  name: string;
  position: number;
}

/** Who saved something: a member, or a member's API key acting as them. */
export interface PolicyActor {
  user_id: string | null;
  name: string | null;
  via_api_key_name: string | null;
}

export interface Policy {
  id: string;
  section_id: string;
  position: number;
  title: string;
  kind: PolicyKind;
  status: PolicyStatus;
  /** Rich text as HTML. */
  body: string;
  version: number;
  updated_at: string;
  updated_by: PolicyActor;
  archived_at: string | null;
  /** Who archived it, while it's archived. */
  archived_by_name: string | null;
}

/** A top-bar search result. */
export interface PolicySearchHit {
  id: string;
  title: string;
  kind: PolicyKind;
  status: PolicyStatus;
  section_name: string;
  archived: boolean;
}

export interface PolicyList {
  sections: PolicySection[];
  policies: Policy[];
}

export interface PolicyVersion {
  version: number;
  title: string;
  kind: PolicyKind;
  status: PolicyStatus;
  body: string;
  note: string | null;
  created_at: string;
  actor: PolicyActor;
}

export interface PolicyInput {
  section_id: string;
  title: string;
  kind?: PolicyKind;
  status?: PolicyStatus;
  body?: string;
  note?: string | null;
}

export interface PolicyUpdate {
  title?: string;
  kind?: PolicyKind;
  status?: PolicyStatus;
  body?: string;
  note?: string | null;
  /** The Version the edit started from; a save on top of someone else's newer Version is refused. */
  expected_version?: number;
}
